import { DatePicker, DatePickerSelectedValue } from '@storeblocks/datepicker';
import { ChangeEvent, type FC, useState } from 'react';

import { useTexts } from '@/hooks/useTexts';
import { useUserLocale } from '@/i18n/hooks';

interface Props {
  label: string;
  onChange: (date: DatePickerSelectedValue) => void;
}

export const DateSearch: FC<Props> = ({ label, onChange }: Props) => {
  const texts = useTexts();
  const locale = useUserLocale();

  const [searchDate, setSearchDate] = useState<Date | undefined>();
  const [errorMessage, setErrorMessage] = useState('');

  const handleDateOnChange = (
    _: ChangeEvent<HTMLInputElement>,
    selectedDate: DatePickerSelectedValue,
  ): void => {
    // If the datepicker is cleared; reset the search date and clear the error message.
    if (selectedDate.dateString === '') {
      setSearchDate(undefined);
      onChange(selectedDate);
      setErrorMessage('');
    }
    // If the datepicker is set to an invalid date, show an error message.
    else if (!selectedDate.isValid) {
      setErrorMessage(texts.reportsArchivePage.dateSearch.error);
    }
    // If the datepicker is set to a valid date, update the search date and clear the error message.
    else {
      setSearchDate(selectedDate.date);
      onChange(selectedDate);
      setErrorMessage('');
    }
  };

  return (
    <DatePicker
      id="search-by-date"
      name="search-by-date"
      selectMonthAriaLabel={texts.datepicker.selectMonthAriaLabel}
      selectYearAriaLabel={texts.datepicker.selectYearAriaLabel}
      aria-label={label}
      labelText={label}
      locale={locale}
      alertMessageType={errorMessage ? 'error' : undefined}
      alertMessage={errorMessage}
      data-trackid-source="presentation-filter"
      data-trackid-type="date-picker"
      value={searchDate}
      onChange={handleDateOnChange}
      clearable
      clearLabel={texts.reportsArchivePage.dateSearch.clearLabel}
      size="medium"
    />
  );
};
